import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, dashboardExtendedAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { fmt } from '../lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { RecentlyViewedCard } from '../components/RecentlyViewedCard';
import { 
  Users, 
  CalendarDays, 
  AlertTriangle, 
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  Briefcase,
  Shield,
  ChevronRight
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { format, parseISO } from 'date-fns';
import { InstallButton } from '../components/InstallButton';
import InvitationsSummary from '../components/InvitationsSummary';

const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = '#1ABC9C', onClick }) => (
  <Card 
    className={`bg-[#2B2B2B] border-[#3D3D3D] hover:border-[#1ABC9C]/30 transition-colors shadow-light ${onClick ? 'cursor-pointer hover:bg-[#333333]' : ''}`}
    onClick={onClick}
    data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-[#C7C7C7] mb-1">{title}</p>
          <p className="text-3xl font-bold text-[#F2F2F2]">{value}</p>
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              <TrendingUp className={`w-4 h-4 ${trend === 'up' ? 'text-[#1ABC9C]' : 'text-red-400'}`} />
              <span className={`text-sm ${trend === 'up' ? 'text-[#1ABC9C]' : 'text-red-400'}`}>
                {trendValue}
              </span>
            </div>
          )}
        </div>
        <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const COLORS = ['#1ABC9C', '#FFC04D', '#ef4444', '#6366f1'];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgCurrency = user?.organisation_currency || 'GBP';
  const [stats, setStats] = useState(null);
  const [coverage, setCoverage] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [finance, setFinance] = useState(null);
  const [jobTypeStats, setJobTypeStats] = useState([]);
  const [contractStats, setContractStats] = useState([]);
  const [docExpiry, setDocExpiry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, coverageRes, complianceRes, financeRes, jobTypeRes, contractRes, docExpiryRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getShiftCoverage(7),
        dashboardAPI.getComplianceSummary(),
        dashboardAPI.getFinanceSummary(),
        dashboardExtendedAPI.getShiftsByJobType(),
        dashboardExtendedAPI.getShiftsByContract(),
        dashboardExtendedAPI.getDocumentExpirySummary().catch(() => ({ data: null })),
      ]);
      
      setStats(statsRes.data);
      setCoverage(coverageRes.data);
      setCompliance(complianceRes.data);
      setFinance(financeRes.data);
      setJobTypeStats(jobTypeRes.data);
      setContractStats(contractRes.data);
      setDocExpiry(docExpiryRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  const complianceData = compliance ? [
    { name: 'Compliant', value: compliance.compliant, color: '#1ABC9C' },
    { name: 'Expiring Soon', value: compliance.expiring_soon, color: '#FFC04D' },
    { name: 'Expired', value: compliance.expired, color: '#ef4444' },
    { name: 'Pending', value: compliance.pending, color: '#6366f1' },
  ].filter(item => item.value > 0) : [];

  return (
    <div className="space-y-6" data-testid="dashboard-container">
      <div className="flex justify-end -mt-2 -mb-2">
        <InstallButton
          variant="outline"
          size="sm"
          className="border-[#1ABC9C]/40 text-[#1ABC9C] hover:bg-[#1ABC9C]/10"
          label="Install App"
          testId="dashboard-install-btn"
        />
      </div>

      {/* Admin-only: team-invitation conversion widget */}
      <InvitationsSummary />
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Medics"
          value={stats?.total_medics || 0}
          icon={Users}
          color="#1ABC9C"
          onClick={() => navigate('/medics')}
        />
        <StatCard
          title="Compliant Medics"
          value={stats?.compliant_medics || 0}
          icon={CheckCircle}
          color="#1ABC9C"
          onClick={() => navigate('/medics?status=compliant')}
        />
        <StatCard
          title="Expiring Soon"
          value={stats?.expiring_soon_medics || 0}
          icon={AlertTriangle}
          color="#FFC04D"
          onClick={() => navigate('/compliance?filter=expiring_soon')}
        />
        <StatCard
          title="Expired Documents"
          value={stats?.expired_medics || 0}
          icon={XCircle}
          color="#ef4444"
          onClick={() => navigate('/compliance?filter=expired')}
        />
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Today's Shifts"
          value={stats?.today_shifts || 0}
          icon={CalendarDays}
          color="#6366f1"
          onClick={() => navigate('/shifts?filter=today')}
        />
        <StatCard
          title="Pending Shifts"
          value={stats?.pending_shifts || 0}
          icon={Clock}
          color="#FFC04D"
          onClick={() => navigate('/shifts?status=pending')}
        />
        <StatCard
          title="Contracts"
          value={stats?.total_contracts || 0}
          icon={FileText}
          color="#1ABC9C"
          onClick={() => navigate('/contracts')}
        />
        <StatCard
          title="Job Types"
          value={stats?.total_job_types || 0}
          icon={Briefcase}
          color="#FFC04D"
          onClick={() => navigate('/job-types')}
        />
      </div>

      {/* iter72.5 — recently-viewed records surface above the charts
          so admins return to a shift/medic/contract in one click. The
          card renders nothing until the user has opened something. */}
      <RecentlyViewedCard />

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Shift Coverage Chart */}
        <Card className="bg-[#2B2B2B] border-[#3D3D3D] lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-[#F2F2F2] text-lg">
              7-Day Shift Coverage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%" minHeight={280}>
                <BarChart data={coverage}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#C7C7C7" 
                    tick={{ fill: '#C7C7C7', fontSize: 12 }}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { weekday: 'short' })}
                  />
                  <YAxis stroke="#C7C7C7" tick={{ fill: '#C7C7C7', fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#2B2B2B', 
                      border: '1px solid #3D3D3D',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: '#F2F2F2' }}
                  />
                  <Bar dataKey="assigned" name="Assigned" fill="#1ABC9C" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="unassigned" name="Unassigned" fill="#FFC04D" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Compliance Pie Chart */}
        <Card className="bg-[#2B2B2B] border-[#3D3D3D]">
          <CardHeader>
            <CardTitle className="text-[#F2F2F2] text-lg">
              Compliance Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72 flex items-center justify-center">
              {compliance?.total > 0 ? (
                <ResponsiveContainer width="100%" height="100%" minHeight={280}>
                  <PieChart>
                    <Pie
                      data={complianceData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {complianceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#2B2B2B', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-[#C7C7C7]">No data available</p>
              )}
            </div>
            {/* Legend */}
            <div className="flex justify-center gap-4 mt-4">
              {complianceData.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm text-[#C7C7C7]">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Finance Summary */}
      <Card className="bg-[#2B2B2B] border-[#3D3D3D]">
        <CardHeader>
          <CardTitle className="text-[#F2F2F2] text-lg">
            Expense Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-[#1A1A1A] rounded-lg border border-[#3D3D3D]">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#FFC04D]/15">
                  <Clock className="w-5 h-5 text-[#FFC04D]" />
                </div>
                <div>
                  <p className="text-sm text-[#C7C7C7]">Pending</p>
                  <p className="text-xl font-bold text-[#F2F2F2]">
                    {fmt(finance?.pending || 0, orgCurrency)}
                  </p>
                  <p className="text-xs text-[#C7C7C7]">{finance?.pending_count || 0} items</p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-[#1A1A1A] rounded-lg border border-[#3D3D3D]">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                  <CheckCircle className="w-5 h-5 text-[#1ABC9C]" />
                </div>
                <div>
                  <p className="text-sm text-[#C7C7C7]">Approved</p>
                  <p className="text-xl font-bold text-[#F2F2F2]">
                    {fmt(finance?.approved || 0, orgCurrency)}
                  </p>
                  <p className="text-xs text-[#C7C7C7]">{finance?.approved_count || 0} items</p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-[#1A1A1A] rounded-lg border border-[#3D3D3D]">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/15">
                  <XCircle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-sm text-[#C7C7C7]">Rejected</p>
                  <p className="text-xl font-bold text-[#F2F2F2]">
                    {fmt(finance?.rejected || 0, orgCurrency)}
                  </p>
                  <p className="text-xs text-[#C7C7C7]">{finance?.rejected_count || 0} items</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document Expiry Widget */}
      {docExpiry && (
        <Card className="bg-[#2B2B2B] border-[#3D3D3D]" data-testid="doc-expiry-widget">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-[#F2F2F2] text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#1ABC9C]" />
              Document Compliance Alerts
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              className="text-[#1ABC9C] hover:text-[#1ABC9C]/80"
              onClick={() => navigate('/compliance')}
            >
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Expired Documents */}
              <div className="p-4 bg-[#1A1A1A] rounded-lg border border-red-500/20">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-400" />
                    <span className="font-medium text-red-400">Expired</span>
                  </div>
                  <Badge className="bg-red-500/15 text-red-400 border-red-500/20">
                    {docExpiry.expired?.count || 0}
                  </Badge>
                </div>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {docExpiry.expired?.documents?.length > 0 ? (
                    docExpiry.expired.documents.slice(0, 3).map((doc, idx) => (
                      <div key={idx} className="text-sm">
                        <p className="text-[#F2F2F2] truncate">{doc.document_type_name}</p>
                        <p className="text-[#A1A1AA] text-xs truncate">{doc.medic_name}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-[#A1A1AA]">No expired documents</p>
                  )}
                </div>
              </div>

              {/* Expiring This Week */}
              <div className="p-4 bg-[#1A1A1A] rounded-lg border border-[#FFC04D]/20">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-[#FFC04D]" />
                    <span className="font-medium text-[#FFC04D]">This Week</span>
                  </div>
                  <Badge className="bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20">
                    {docExpiry.expiring_this_week?.count || 0}
                  </Badge>
                </div>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {docExpiry.expiring_this_week?.documents?.length > 0 ? (
                    docExpiry.expiring_this_week.documents.slice(0, 3).map((doc, idx) => (
                      <div key={idx} className="text-sm">
                        <p className="text-[#F2F2F2] truncate">{doc.document_type_name}</p>
                        <p className="text-[#A1A1AA] text-xs">
                          {doc.medic_name} • {doc.expires_at ? format(parseISO(doc.expires_at), 'MMM d') : ''}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-[#A1A1AA]">None expiring this week</p>
                  )}
                </div>
              </div>

              {/* Expiring This Month */}
              <div className="p-4 bg-[#1A1A1A] rounded-lg border border-blue-500/20">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-400" />
                    <span className="font-medium text-blue-400">This Month</span>
                  </div>
                  <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/20">
                    {docExpiry.expiring_this_month?.count || 0}
                  </Badge>
                </div>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {docExpiry.expiring_this_month?.documents?.length > 0 ? (
                    docExpiry.expiring_this_month.documents.slice(0, 3).map((doc, idx) => (
                      <div key={idx} className="text-sm">
                        <p className="text-[#F2F2F2] truncate">{doc.document_type_name}</p>
                        <p className="text-[#A1A1AA] text-xs">
                          {doc.medic_name} • {doc.expires_at ? format(parseISO(doc.expires_at), 'MMM d') : ''}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-[#A1A1AA]">None expiring this month</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
